import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { from, Observable, throwError, of } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new Observable<string>();

  constructor(private authService: AuthService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const currentUser = this.authService.currentUser;
    const isApiRequest = this.isApiRequest(request.url);

    if (isApiRequest && (currentUser?.token || currentUser?.accessToken)) {
      if(currentUser.provider === 'TEAM') {
        request = this.addTokenHeader(request, currentUser.accessToken, currentUser);
      }
      else {
      request = this.addTokenHeader(request, currentUser.token, currentUser);
      }
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Detectamos errores de autenticación
        if (isApiRequest && [401, 403, 0].includes(error.status)) {
          // Solo renovamos si el provider no es TEAM
          if (currentUser && currentUser.provider !== 'TEAM') {
            return this.handle401Error(request, next);
          }
        }
        return throwError(() => error);
      })
    );
  }

  /** Los proveedores de identidad rechazan por CORS los headers propios de la app. */
  private isApiRequest(url: string): boolean {
    const apiBase = (environment.apiUrl && environment.apiUrl.length > 0)
      ? environment.apiUrl
      : window.location.origin;

    try {
      const target = new URL(url, window.location.origin);
      return target.origin === new URL(apiBase).origin && target.pathname.startsWith('/api/');
    } catch {
      return false;
    }
  }

  /** Agrega el header de Authorization al request */
  private addTokenHeader(request: HttpRequest<any>, token: string, user: any): HttpRequest<any> {
    let headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (user.provider === 'TEAM' && user.ad_username) {
      headers['AD_UserName'] = user.ad_username;
    }

    if (user.provider === 'TEAM') {
      headers['X-Token-Issuer'] = 'TEAM';
    }

    return request.clone({ setHeaders: headers });
  }

  /** Maneja el error 401 con reintento tras refresh */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      // Creamos un observable compartido para evitar múltiples refresh simultáneos
      const refresh$ = from(this.authService.verifyAndRenewToken()).pipe(
        switchMap((newToken: string | null) => {
          this.isRefreshing = false;

          if (newToken) {
            const newUser = this.authService.currentUser;
            const newRequest = this.addTokenHeader(request, newToken, newUser);
            return next.handle(newRequest);
          } else {
            console.error('No token recibido tras el refresh.');
            this.authService.loginRedirectAd();
            return throwError(() => new Error('No token after refresh'));
          }
        }),
        catchError((refreshError) => {
          console.error('Error al renovar token:', refreshError);
          this.isRefreshing = false;
          this.authService.loginRedirectAd();
          return throwError(() => refreshError);
        })
      );

      return refresh$;
    } else {
      // Si ya hay un refresh en curso, esperamos a que termine
      return this.refreshTokenSubject.pipe(
        filter((token) => !!token),
        take(1),
        switchMap((token) => {
          const newUser = this.authService.currentUser;
          const newRequest = this.addTokenHeader(request, token, newUser);
          return next.handle(newRequest);
        })
      );
    }
  }
}
